/**
 * Renders Template Studio specs with Herald fallbacks plus report chrome components.
 * Uses {@link templateStudioRendererRegistry}; no chat export or dev-tools wrappers.
 */
import type { ComponentRenderProps } from "@json-render/react";
import {
  ActionProvider,
  Renderer,
  StateProvider,
  useStateStore,
  VisibilityProvider,
} from "@json-render/react";
import { type ReactNode, useMemo } from "react";
import { ReportThemeProvider } from "@/components/ui/report/ReportThemeProvider";
import { createHeraldRendererHandlers } from "@/lib/chat/heraldRendererRegistry";
import { migrateLegacyHeraldSpec } from "@/lib/chat-sanitize/migrateLegacyHeraldSpec";
import { sanitizeSpecRefs } from "@/lib/chat-sanitize/sanitizeSpecRefs";
import type { ResolvedTemplateStudioReportTheme } from "@/lib/template-studio/reportThemeFromLayoutTheme";
import { templateStudioRendererRegistry } from "@/lib/template-studio/templateStudioRendererRegistry";
import { useGoogleFont } from "@/lib/template-studio/useGoogleFont";

/** Fallback when the spec references a component not in the Template Studio registry. */
const UnknownTemplateStudioComponentFallback = ({
  element,
}: ComponentRenderProps) => (
  <div
    className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
    role="alert"
  >
    Unknown or unsupported component: <strong>{element.type}</strong>. This
    component is not available in Template Studio.
  </div>
);

interface TemplateStudioSpecRendererProps {
  spec: Parameters<typeof Renderer>[0]["spec"];
  fallback?: Parameters<typeof Renderer>[0]["fallback"];
  /** Persisted layout theme resolved for {@link ReportThemeProvider} and font loading. */
  theme?: ResolvedTemplateStudioReportTheme;
}

/** Wires action handlers from the template registry (must be inside StateProvider). */
const TemplateStudioActionProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const store = useStateStore();
  const handlers = useMemo(() => {
    const setState = (
      updater: (prev: Record<string, unknown>) => Record<string, unknown>,
    ) => {
      store.update(updater(store.getSnapshot() ?? {}));
    };
    return createHeraldRendererHandlers(
      () => setState,
      () => store.getSnapshot() ?? {},
    );
  }, [store]);
  return <ActionProvider handlers={handlers}>{children}</ActionProvider>;
};

/** Renderer for Template Studio generated specs. */
export const TemplateStudioSpecRenderer = ({
  spec,
  fallback,
  theme,
}: TemplateStudioSpecRendererProps) => {
  useGoogleFont(theme?.fontStylesheetUrl ?? null);

  const sanitized = useMemo(() => sanitizeSpecRefs(spec), [spec]);
  const migrated = useMemo(
    () => migrateLegacyHeraldSpec(sanitized),
    [sanitized],
  );

  if (!migrated?.root || !migrated?.elements) {
    return null;
  }

  return (
    <StateProvider initialState={migrated.state ?? {}}>
      <TemplateStudioActionProvider>
        <VisibilityProvider>
          <ReportThemeProvider theme={theme?.reportTheme} className="w-full">
            <div
              className={
                "w-full text-sm " +
                "**:data-[slot=card]:text-sm! **:data-[slot=card-content]:text-sm! " +
                "**:data-[slot=card-title]:text-base! **:data-[slot=card-description]:text-sm!"
              }
            >
              <Renderer
                spec={migrated}
                registry={templateStudioRendererRegistry}
                fallback={fallback ?? UnknownTemplateStudioComponentFallback}
              />
            </div>
          </ReportThemeProvider>
        </VisibilityProvider>
      </TemplateStudioActionProvider>
    </StateProvider>
  );
};
